import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Button } from 'react-toolbox/lib/button';
import Dropdown from 'react-toolbox/lib/dropdown';
import ComposeWindow from 'components/ComposeWindow';
import TextArea from 'components/TextArea';
import Validator from 'mixins/validator';
import Resetter from 'mixins/resetter';
import PhaseType from 'types/phase';
import { createSupportRequest } from 'actionCreators/phase';
import { getPhaseErrors } from '../../../../../../reducers';
import S from 'components/ComposeWindow/styles.scss';

const CATEGORY_OPTIONS = [
  { value: 'LABOR', label: 'Labor' },
  { value: 'PRODUCTS', label: 'Products' },
  { value: 'REFUNDS', label: 'Refunds' },
  { value: 'MISSING_DOCUMENTS', label: 'Missing Documents' },
  { value: 'CUSTOMERS', label: 'Customer' },
  { value: 'PERMITS', label: 'Permits' },
];

const TYPE_OPTIONS_BY_CATEGORY = {
  Installation: {
    LABOR: ['MILEAGE', 'TRIP_FEE', 'MISSING_OR_INCORRECT_LABOR'],
    PRODUCTS: ['PRODUCT_NOT_READY_AT_JOBSITE', 'PRODUCT_NOT_READY_AT_STORE', 'DAMAGED_PRODUCT', 'MISSING_OR_INCORRECT_PRODUCT'],
    REFUNDS: ['FULL_REFUND', 'PARTIAL_REFUND', 'PERMIT_REFUND'],
    MISSING_DOCUMENTS: ['PROVIDE_DESIGNS', 'PROVIDE_PROPOSALS', 'PROVIDE_CONTRACT'],
    CUSTOMERS: ['UNABLE_TO_CONTACT_CUSTOMER', 'INCORRECT_CONTACT_INFORMATION'],
    PERMITS: ['ADD_PERMIT', 'EXTEND_PERMIT'],
  },
  Assessment: {
    LABOR: ['MILEAGE', 'TRIP_FEE', 'MISSING_OR_INCORRECT_LABOR'],
    PRODUCTS: [],
    REFUNDS: ['FULL_REFUND', 'PARTIAL_REFUND'],
    MISSING_DOCUMENTS: ['PROVIDE_DESIGNS', 'PROVIDE_PROPOSALS'],
    CUSTOMERS: ['UNABLE_TO_CONTACT_CUSTOMER', 'INCORRECT_CONTACT_INFORMATION'],
    PERMITS: [],
  },
  Workorder: {
    LABOR: ['MILEAGE', 'TRIP_FEE', 'MISSING_OR_INCORRECT_LABOR'],
    PRODUCTS: ['PRODUCT_NOT_READY_AT_JOBSITE', 'PRODUCT_NOT_READY_AT_STORE', 'DAMAGED_PRODUCT', 'MISSING_OR_INCORRECT_PRODUCT'],
    REFUNDS: ['PERMIT_REFUND'],
    MISSING_DOCUMENTS: ['PROVIDE_DESIGNS', 'PROVIDE_PROPOSALS', 'PROVIDE_CONTRACT'],
    CUSTOMERS: ['UNABLE_TO_CONTACT_CUSTOMER', 'INCORRECT_CONTACT_INFORMATION'],
    PERMITS: ['ADD_PERMIT', 'EXTEND_PERMIT'],
  },
};

const PAYMENT_TYPE_OPTIONS = [
  { value: 'TRIP_FEE', label: 'Trip Fee' },
  { value: 'TRIP_FEE_AND_WAIT_FEE', label: 'Trip and Wait Fee' },
  { value: 'WAIT_FEE', label: 'Wait Fee' },
  { value: 'NONE', label: 'None' },
];

const MAX_DESCRIPTION_LENGTH = 250;

const requiresPaymentType = supportCategory =>
    supportCategory === 'PRODUCTS';

// Converts snake_case or UPPER_CASE strings into Title Case for display.
const toTitleCase = value =>
  value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());

const friendlyLabel = value => toTitleCase(value);

class CreateSupportRequestModal extends Resetter(Validator(React.Component)) {
  static DEFAULT_STATE = {
    supportCategory: '',
    supportType: '',
    supportDescription: '',
    exceptionPaymentType: '',
    errors: {},
    submitting: false,
  };

  static VALIDATIONS = {
    supportCategory: true,
    supportType: true,
    supportDescription: true,
  };

  handleSubmit = () => {
    this.setState({ submitting: true }, () => {
      const baseValid = this.validate();
      const needsPaymentType =
        requiresPaymentType(this.state.supportCategory) && !this.state.exceptionPaymentType;

      if (!baseValid || needsPaymentType) {
        if (needsPaymentType) {
          this.setState(({ errors }) => ({
            errors: { ...errors, exceptionPaymentType: ['Required'] },
            submitting: false,
          }));
          return;
        }
        this.setState({ submitting: false });
        return;
      }

      const { supportCategory, supportType, supportDescription, exceptionPaymentType } = this.state;
      const payload = {
        supportCategory,
        supportType,
        supportDescription,
        exceptionPaymentType,
      };

      if (requiresPaymentType(supportCategory)) {
        payload.exceptionPaymentType = exceptionPaymentType;
      }

      this.props.createSupportRequest(this.props.phase.id, payload)
        .then(() => {
          this.handleClose();
        })
        .catch(() => {
          this.setState({ submitting: false });
        });
    });
  };

  handleClose = () => {
    this.setState(CreateSupportRequestModal.DEFAULT_STATE);
    this.props.onClose();
  };

  handleCategoryChange = (value) => {
    this.setState({
      supportCategory: value,
      supportType: '',
      exceptionPaymentType: '',
    });
  };

  handleSupportTypeChange = (value) => {
    this.setState({
      supportType: value,
      exceptionPaymentType: '',
    });
  };

  render() {
    const { active, phase } = this.props;
    const {
      supportCategory, supportType, exceptionPaymentType, errors, submitting,
    } = this.state;

    const typeOptions = TYPE_OPTIONS_BY_CATEGORY[phase.type] || {};
    const supportTypeOptions = (typeOptions[supportCategory] || []).map(value => ({
      value,
      label: friendlyLabel(value),
    }));

    return (
      <ComposeWindow
        active={active}
        title="Create Support Request"
        onClose={this.handleClose}
      >
        <Dropdown
          label="Choose a topic *"
          source={CATEGORY_OPTIONS}
          value={supportCategory}
          onChange={this.handleCategoryChange}
          error={(errors.supportCategory || [])[0]}
        />
        <Dropdown
          label="What do you need help with? *"
          source={supportTypeOptions}
          value={supportType}
          onChange={this.handleSupportTypeChange}
          error={(errors.supportType || [])[0]}
          disabled={!supportCategory}
        />
        <TextArea
          label="Describe your issue *"
          value={this.state.supportDescription}
          // Defensive: handle both string and event
          onChange={e => this.setState({ supportDescription: e.target ? e.target.value : e })}
          className={S.compose}
          errors={errors.supportDescription}
          maxLength={MAX_DESCRIPTION_LENGTH}
          autoFocus
          ref={(comp) => { this.textAreaComponentRef = comp; }}
        />
        {requiresPaymentType(supportCategory) && (
          <Dropdown
            label="Exception payment type *"
            source={PAYMENT_TYPE_OPTIONS}
            value={exceptionPaymentType}
            onChange={value => this.setState({ exceptionPaymentType: value })}
            error={(errors.exceptionPaymentType || [])[0]}
          />
        )}
        <div className={S.actions}>
          <Button
            label="Create Support Request"
            primary
            flat
            ripple
            disabled={submitting}
            className={S.submit}
            onClick={this.handleSubmit}
          />
        </div>
      </ComposeWindow>
    );
  }
}

CreateSupportRequestModal.propTypes = {
  active: PropTypes.bool.isRequired,
  phase: PhaseType.isRequired,
  onClose: PropTypes.func.isRequired,
  createSupportRequest: PropTypes.func.isRequired,
};

export default connect(
  state => ({ errors: getPhaseErrors(state) }),
  { createSupportRequest },
)(CreateSupportRequestModal);




// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/createSupportRequestModal.jsx